import { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { hapticTap, hapticSuccess, hapticError } from "../utils/haptics";
import { getCached, setCached, addRecentTicker, getRecentTickers, getCachedChain, setCachedChain, getCachedExpirations, setCachedExpirations, getFavorites, toggleFavorite, isFavorite } from "../utils/symbolCache";
import { ExecutionConfirmModal, MultiLegExecutionConfirmModal } from "./modals/ExecutionConfirmModal";
// Same real Omega-curated watchlist + real quotes pattern the desktop
// Trading Hub's Discover section uses (TradingHubTab.js) — reused here so
// the mobile search step surfaces real picks with real reasons instead of
// a bare list of ticker symbols with no signal attached.
import { OMEGA_PICKS_LIST } from "./OmegaAIPicksPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══ Formatters ═════════════════════════════════════════════════════════════
const f$ = (v, d = 2) => { if (v == null || isNaN(v)) return "—"; const n = Number(v); if (Math.abs(n) >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T"; if (Math.abs(n) >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B"; if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M"; if (Math.abs(n) >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K"; return "$" + n.toFixed(d); };
const fN = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d);
const fInt = (v) => (v == null || isNaN(v)) ? "—" : Number(v).toLocaleString("en-US");
const fPct = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d) + "%";
const fBig = (v) => { if (v == null || isNaN(v)) return "—"; const n = Math.abs(Number(v)); if (n >= 1e12) return (n / 1e12).toFixed(2) + "T"; if (n >= 1e9) return (n / 1e9).toFixed(2) + "B"; if (n >= 1e6) return (n / 1e6).toFixed(2) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return n.toFixed(0); };

const timeAgo = (iso) => { if (!iso) return ""; const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000); if (s < 60) return s + "s"; if (s < 3600) return Math.floor(s / 60) + "m"; if (s < 86400) return Math.floor(s / 3600) + "h"; return Math.floor(s / 86400) + "d"; };

// ═══ STEP 1: SEARCH ═════════════════════════════════════════════════════════
function SearchStep({ onSelect }) {
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [discoverQuotes, setDiscoverQuotes] = useState({}); // symbol -> {price, changePct}
  const popular = ["AAPL", "TSLA", "NVDA", "SPY", "QQQ", "AAL", "BAC", "F", "AMZN", "MSFT", "META", "SOFI"];
  useEffect(() => {
    setRecent(getRecentTickers().slice(0, 8));
    setFavorites(getFavorites());
    // /market/quotes has no prior-close field to derive a real change% from —
    // same honest "price only, no fabricated change%" pattern the desktop
    // Discover section uses, rather than inventing one.
    const syms = OMEGA_PICKS_LIST.map(p => p.symbol).join(",");
    axios.get(`${API}/market/quotes?symbols=${syms}`).then(r => {
      const map = {};
      (Array.isArray(r.data) ? r.data : []).forEach(q => { if (q?.symbol) map[q.symbol] = q; });
      setDiscoverQuotes(map);
    }).catch(() => {});
  }, []);
  const pick = (t) => { hapticTap(); addRecentTicker(t); onSelect(t); };
  const onToggleFav = (t, e) => {
    e.stopPropagation();
    hapticTap();
    setFavorites(toggleFavorite(t));
  };
  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>Search</div>
      <div style={{ fontSize: 13, color: "#636e7b", marginBottom: 16 }}>Find a stock to trade options</div>
      <form onSubmit={e => { e.preventDefault(); if (query.trim()) pick(query.toUpperCase()); }} style={{ marginBottom: 24 }}>
        <input value={query} onChange={e => setQuery(e.target.value.toUpperCase())} placeholder="Search ticker or company..." autoFocus
          style={{ width: "100%", padding: "14px 18px", borderRadius: 14, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#e6edf3", fontSize: 16, outline: "none", boxSizing: "border-box" }}
          data-testid="option-search-input" />
      </form>

      {favorites.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#fbbf24", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Favorites · instant load
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
            {favorites.map(t => (
              <div key={t} onClick={() => pick(t)} data-testid={`fav-${t}`} style={{ padding: "8px 14px", borderRadius: 10, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.30)", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#fbbf24", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#fbbf24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                {t}
              </div>
            ))}
          </div>
        </>
      )}

      {recent.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#60a5fa", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Recent · tap for instant load
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
            {recent.map(t => (
              <div key={t} onClick={() => pick(t)} data-testid={`recent-${t}`} style={{ padding: "8px 14px", borderRadius: 10, background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.24)", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#60a5fa", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {t}
                <span onClick={(e) => onToggleFav(t, e)} data-testid={`fav-toggle-${t}`} style={{ cursor: "pointer", color: favorites.includes(t) ? "#fbbf24" : "rgba(255,255,255,0.2)", fontSize: 11, lineHeight: 1 }} title={favorites.includes(t) ? "Unfavorite" : "Add to favorites"}>
                  {favorites.includes(t) ? "★" : "☆"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {/* Ω Discover — same real curated watchlist + real quotes the desktop
          Trading Hub's Discover section shows, so mobile search opens on
          real picks with real reasons instead of bare ticker chips. */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "#a78bfa", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa" }} />
        Ω Discover
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 22 }} data-testid="otf-discover-list">
        {OMEGA_PICKS_LIST.map(p => {
          const q = discoverQuotes[p.symbol];
          return (
            <div key={p.symbol} onClick={() => pick(p.symbol)} data-testid={`discover-${p.symbol}`}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 12, cursor: "pointer",
                background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.14)",
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>{p.symbol}</div>
                <div style={{ fontSize: 10.5, color: "#8b949e", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.reason}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#c9d1d9", flexShrink: 0 }}>{q?.price != null ? `$${fN(q.price, 2)}` : "—"}</div>
              <span onClick={(e) => onToggleFav(p.symbol, e)} data-testid={`fav-toggle-discover-${p.symbol}`} style={{ cursor: "pointer", color: favorites.includes(p.symbol) ? "#fbbf24" : "rgba(255,255,255,0.2)", fontSize: 13, lineHeight: 1, flexShrink: 0 }} title={favorites.includes(p.symbol) ? "Unfavorite" : "Add to favorites"}>
                {favorites.includes(p.symbol) ? "★" : "☆"}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Popular</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {popular.map(t => (
          <div key={t} onClick={() => pick(t)} style={{ padding: "10px 18px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#e6edf3" }} data-testid={`popular-${t}`}>{t}</div>
        ))}
      </div>
    </div>
  );
}

// ═══ NEWS DETAIL MODAL (Omega ZI AI analysis) ═══════════════════════════════
function NewsDetailModal({ article, ticker, changePct, onClose }) {
  const [aiSummary, setAiSummary] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prompt = `News Headline: "${article.title}" (Source: ${article.source || "unknown"}, Date: ${article.pubDate || "recent"}) for ${ticker} stock (currently ${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}% today).

Provide a concise 3-4 sentence analysis covering:
1. What this news actually means for ${ticker}
2. Likely market reaction and sentiment direction (bullish/bearish/neutral)
3. Specific options trading implications (IV expectations, directional bias, DTE considerations)

Be direct, actionable, and avoid filler.`;
    setLoading(true);
    axios.post(`${API}/zi/chat`, { message: prompt, sessionId: `news-${Date.now()}` })
      .then(r => setAiSummary(r.data?.response || ""))
      .catch(() => setAiSummary(`**${ticker}** — Headline suggests ${changePct >= 0 ? "positive" : "negative"} pressure. Monitor for IV movement near the event. Consider shorter DTE entries on strong conviction.`))
      .finally(() => setLoading(false));
  }, [article.title, ticker, article.source, article.pubDate, changePct]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 10002, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 0 }} data-testid="news-detail-modal">
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 500, maxHeight: "88vh", overflowY: "auto", background: "#0d1117", borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "20px 20px 32px", boxShadow: "0 -8px 32px rgba(0,0,0,0.6)", position: "relative" }}>
        {/* Sticky floating X close at top-right — always reachable regardless of scroll */}
        <button
          onClick={onClose}
          data-testid="news-modal-x"
          aria-label="Close"
          style={{
            position: "sticky", top: 8, float: "right",
            width: 36, height: 36, borderRadius: 18,
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
            color: "#e6edf3", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 5, marginTop: -8,
            WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.12)" }} /></div>

        {/* Source + time */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 11, color: "#636e7b" }}>
          {article.source && <span style={{ fontWeight: 700, color: "#8b949e", textTransform: "uppercase", letterSpacing: 0.5 }}>{article.source}</span>}
          {article.pubDate && <span>· {new Date(article.pubDate).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
        </div>

        {/* Title */}
        <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", lineHeight: 1.3, marginBottom: 16 }}>{article.title}</div>

        {/* Ticker badge */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, background: changePct >= 0 ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)", marginBottom: 20, border: "1px solid " + (changePct >= 0 ? "rgba(74,222,128,0.18)" : "rgba(239,68,68,0.18)") }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>${ticker}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: changePct >= 0 ? "#4ade80" : "#f87171" }}>{changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%</span>
        </div>

        {/* Omega ZI AI Analysis */}
        <div style={{ padding: 16, borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.05))", border: "1px solid rgba(167,139,250,0.2)", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 11, fontWeight: 800, color: "#a78bfa", letterSpacing: 1 }}>OMEGA ZI AI · NEWS ANALYSIS</span>
          </div>
          {loading ? (
            <div style={{ fontSize: 12, color: "#636e7b" }}><span className="spin" /> Analyzing news impact on {ticker}...</div>
          ) : (
            <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiSummary}</div>
          )}
        </div>

        {/* Optional link out */}
        {article.link && (
          <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 12, borderRadius: 10, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.18)", color: "#60a5fa", fontSize: 12, fontWeight: 600, textDecoration: "none", marginBottom: 12 }}>
            Read full article at {article.source || "source"} ↗
          </a>
        )}

        {/* Close */}
        <button onClick={onClose} data-testid="news-modal-close" style={{ width: "100%", padding: 14, borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#e6edf3", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ═══ STEP 2: SYMBOL INFO (Public.com-style detail page) ═════════════════════
const RANGE_CONFIG = {
  "1D": { days: 0, intraday: true, interval: "5m", range_val: "1d" },
  "1W": { days: 7 },
  "1M": { days: 30 },
  "3M": { days: 90 },
  "YTD": { days: Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000) },
  "1Y": { days: 365 },
  "5Y": { days: 1825 },
};

// ─── Favorite star toggle (used inside SymbolStep header) ────────────────────
function FavoriteStarButton({ ticker }) {
  const [fav, setFav] = useState(false);
  useEffect(() => { setFav(isFavorite(ticker)); }, [ticker]);
  const toggle = () => {
    hapticTap();
    toggleFavorite(ticker);
    setFav(isFavorite(ticker));
  };
  return (
    <button
      data-testid={`symbol-fav-${ticker}`}
      onClick={toggle}
      aria-label={fav ? "Remove from favorites" : "Add to favorites"}
      title={fav ? "Unfavorite" : "Add to favorites"}
      style={{
        width: 40, height: 40, borderRadius: 20,
        background: fav ? "rgba(251,191,36,0.15)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${fav ? "rgba(251,191,36,0.45)" : "rgba(255,255,255,0.08)"}`,
        cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill={fav ? "#fbbf24" : "none"} stroke={fav ? "#fbbf24" : "#8b949e"} strokeWidth="2" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    </button>
  );
}

function SymbolStep({ ticker, onBack, onTradeOptions }) {
  const [profile, setProfile] = useState(null);
  const [quote, setQuote] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [liveBars, setLiveBars] = useState([]); // Client tick accumulator for 1D
  const [range, setRange] = useState("1D");
  const [scan, setScan] = useState(null);
  const [tea, setTea] = useState(null);
  const [news, setNews] = useState([]);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [loadingChart, setLoadingChart] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);

  // TURBO: localStorage cache + stale-while-revalidate — pops up INSTANTLY from cache
  useEffect(() => {
    setLiveBars([]); // reset live accumulator on ticker change

    // 1) Instant paint from localStorage if cache exists
    const cached = getCached(ticker, { news_limit: 8 });
    let fromCache = false;
    if (cached) {
      if (cached.profile) setProfile(cached.profile);
      if (cached.quote) setQuote(cached.quote);
      if (cached.tea) setTea(cached.tea);
      if (cached.news) setNews(cached.news);
      fromCache = true;
      // If cache is super fresh (< 60s) AND has quote, skip the refresh entirely
      if (cached.__isFresh && cached.quote) {
        addRecentTicker(ticker);
        axios.post(`${API}/zi/scan`, { tickers: [ticker] }).then(r => setScan(r.data?.tickers?.[0] || null)).catch(() => {});
        return;
      }
    }

    // 2) Always refresh in background (stale-while-revalidate)
    axios.get(`${API}/market/symbol-bundle?symbol=${ticker}&news_limit=8`).then(r => {
      const d = r.data || {};
      if (d.profile) setProfile(d.profile);
      if (d.quote) setQuote(d.quote);
      if (d.tea) setTea(d.tea);
      if (d.news) setNews(d.news);
      // Cache for next visit
      setCached(ticker, { news_limit: 8 }, { profile: d.profile, quote: d.quote, tea: d.tea, news: d.news });
      addRecentTicker(ticker);
    }).catch(() => {
      if (!fromCache) {
        // Fallback to individual calls only if nothing in cache
        axios.get(`${API}/market/profile?symbol=${ticker}`).then(r => setProfile(r.data)).catch(() => {});
        axios.get(`${API}/tea/score?symbol=${ticker}`).then(r => setTea(r.data)).catch(() => {});
        axios.get(`${API}/market/news/v2?symbol=${ticker}&limit=8&insight=true`).then(r => setNews(r.data?.articles || [])).catch(() => {});
      }
    });
    // Scan is heavier; kick off in parallel
    axios.post(`${API}/zi/scan`, { tickers: [ticker] }).then(r => setScan(r.data?.tickers?.[0] || null)).catch(() => {});
  }, [ticker]);

  // Poll live quote every 4s + append tick to live accumulator
  useEffect(() => {
    const poll = () => axios.get(`${API}/market/quotes?symbols=${ticker}`).then(r => {
      const q = Array.isArray(r.data) ? r.data[0] : r.data;
      if (q?.price) {
        setQuote(q);
        setLiveBars(prev => {
          const next = [...prev, { t: Date.now(), c: q.price }];
          // Keep last 2 hours of ticks max
          return next.slice(-300);
        });
      }
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 4000);
    return () => clearInterval(iv);
  }, [ticker]);

  // Load chart via new /market/chart endpoint
  useEffect(() => {
    setLoadingChart(true);
    axios.get(`${API}/market/chart?symbol=${ticker}&range=${range}`).then(r => {
      const bars = r.data?.bars || [];
      if (bars.length < 2) { setChartData(null); return; }
      // For 1D, append client-side live ticks on top
      const merged = range === "1D" && liveBars.length > 0
        ? [...bars, ...liveBars.filter(t => t.t > bars[bars.length - 1].t)]
        : bars;
      const closes = merged.map(b => b.c);
      const up = closes[closes.length - 1] >= closes[0];
      const labels = merged.map(b => {
        const d = new Date(b.t);
        return range === "1D" ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      });
      setChartData({
        labels,
        datasets: [{
          data: closes,
          borderColor: up ? "#16a34a" : "#ef4444",
          backgroundColor: up ? "rgba(22,163,74,0.12)" : "rgba(239,68,68,0.12)",
          fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3,
        }],
      });
    }).catch(() => setChartData(null)).finally(() => setLoadingChart(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, range]);

  // Re-render chart when live bars arrive (1D only)
  useEffect(() => {
    if (range !== "1D" || liveBars.length === 0 || !chartData) return;
    // Rebuild labels/data with latest live ticks
    axios.get(`${API}/market/chart?symbol=${ticker}&range=1D`).then(r => {
      const hist = r.data?.bars || [];
      const lastHistT = hist.length > 0 ? hist[hist.length - 1].t : 0;
      const merged = [...hist, ...liveBars.filter(t => t.t > lastHistT)];
      if (merged.length < 2) return;
      const closes = merged.map(b => b.c);
      const up = closes[closes.length - 1] >= closes[0];
      setChartData({
        labels: merged.map(b => new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
        datasets: [{ data: closes, borderColor: up ? "#16a34a" : "#ef4444", backgroundColor: up ? "rgba(22,163,74,0.12)" : "rgba(239,68,68,0.12)", fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3 }],
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveBars.length]);

  const p = profile || {};
  const q = quote || {};
  const price = q.price ?? p.price ?? 0;
  const bid = q.bid ?? p.bid;
  const ask = q.ask ?? p.ask;
  const bidSize = q.bidSize ?? p.bidSize;
  const askSize = q.askSize ?? p.askSize;
  const volume = q.volume ?? p.volume;
  const prevClose = p.prevClose;
  const change = prevClose ? (price - prevClose) : 0;
  const changePct = prevClose ? ((price - prevClose) / prevClose * 100) : 0;
  const up = change >= 0;
  const PRIMARY = up ? "#16a34a" : "#ef4444";

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(13,17,23,0.95)" } },
    interaction: { mode: "index", intersect: false },
    scales: { x: { display: false }, y: { display: false } },
  };

  return (
    <div style={{ padding: "0 0 100px" }} data-testid="symbol-step">
      {/* Header: Ticker + Logo + Company + Price + Favorite Star */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          {p.logo && <img src={p.logo} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: "cover", background: "#fff" }} onError={e => e.target.style.display = "none"} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#636e7b", letterSpacing: 0.5 }}>{ticker}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#e6edf3", lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis" }}>{p.longName || p.shortName || ticker}</div>
          </div>
          <FavoriteStarButton ticker={ticker} />
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, color: "#e6edf3", lineHeight: 1.1, marginTop: 6 }}>${fN(price, 2)}</div>
        {prevClose && (
          <div style={{ fontSize: 14, color: PRIMARY, fontWeight: 600, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11 }}>{up ? "▲" : "▼"}</span>
            <span>{up ? "+" : ""}${fN(Math.abs(change), 2)} ({up ? "+" : "-"}{fN(Math.abs(changePct), 2)}%)</span>
            <span style={{ color: "#636e7b", fontWeight: 400 }}>Today</span>
          </div>
        )}
      </div>

      {/* Zi Score + Tea Score badges */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {scan?.ziScore != null && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, background: scan.ziScore >= 68 ? "rgba(74,222,128,0.08)" : scan.ziScore >= 53 ? "rgba(96,165,250,0.08)" : "rgba(251,191,36,0.08)", border: "1px solid " + (scan.ziScore >= 68 ? "rgba(74,222,128,0.2)" : scan.ziScore >= 53 ? "rgba(96,165,250,0.2)" : "rgba(251,191,36,0.2)") }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: scan.ziScore >= 68 ? "#4ade80" : scan.ziScore >= 53 ? "#60a5fa" : "#fbbf24" }}>{scan.ziScore}</span>
            <span style={{ fontSize: 10, color: "#8b949e", textTransform: "uppercase", letterSpacing: 0.5 }}>Omega Zi</span>
            {scan.ziClass && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(167,139,250,0.12)", color: "#a78bfa", fontWeight: 700 }}>{scan.ziClass.replace("_", " ")}</span>}
          </div>
        )}
        {tea?.teaScore != null && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, background: tea.teaScore >= 70 ? "rgba(74,222,128,0.06)" : tea.teaScore >= 40 ? "rgba(251,191,36,0.06)" : "rgba(239,68,68,0.06)", border: "1px solid " + (tea.teaScore >= 70 ? "rgba(74,222,128,0.16)" : tea.teaScore >= 40 ? "rgba(251,191,36,0.16)" : "rgba(239,68,68,0.16)") }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: tea.teaScore >= 70 ? "#4ade80" : tea.teaScore >= 40 ? "#fbbf24" : "#ef4444" }}>{tea.teaScore}</span>
            <span style={{ fontSize: 10, color: "#8b949e", textTransform: "uppercase", letterSpacing: 0.5 }}>Omega Tea</span>
            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(167,139,250,0.12)", color: "#a78bfa", fontWeight: 700 }}>{(tea.sentiment || "").toUpperCase()}</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ height: 240, marginBottom: 8, position: "relative" }}>
        {loadingChart ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#636e7b", fontSize: 12 }}><span className="spin" /></div>
        ) : chartData ? (
          <Line data={chartData} options={chartOpts} />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#636e7b", fontSize: 12 }}>Chart unavailable</div>
        )}
      </div>

      {/* Time range tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid rgba(255,255,255,0.04)", paddingBottom: 14 }}>
        {Object.keys(RANGE_CONFIG).map(r => (
          <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`} style={{
            flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 12, fontWeight: range === r ? 700 : 500,
            background: range === r ? "rgba(22,163,74,0.14)" : "transparent",
            color: range === r ? "#16a34a" : "#636e7b",
            border: "none", cursor: "pointer",
          }}>{r}</button>
        ))}
      </div>

      {/* Omega ZI AI Insight (abbreviated, inline) */}
      {scan?.insight && (
        <div style={{ padding: 14, borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.12)", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 0.5 }}>OMEGA ZI AI</span>
            {scan.topPick && <span style={{ marginLeft: "auto", fontSize: 10, color: "#4ade80", fontWeight: 700 }}>${scan.topPick.strike} {scan.topPick.dte}d · PoP {Math.round((scan.topPick.mcPoP || 0) * 100)}%</span>}
          </div>
          <div style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.6 }}>{scan.insight}</div>
        </div>
      )}

      {/* Omega Tea (sentiment abbreviated, inline) */}
      {tea?.teaScore != null && (
        <div style={{ padding: 14, borderRadius: 14, background: "linear-gradient(135deg, rgba(251,191,36,0.06), rgba(22,163,74,0.03))", border: "1px solid rgba(251,191,36,0.12)", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fbbf24", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "#fbbf24", letterSpacing: 0.5 }}>OMEGA TEA · SENTIMENT</span>
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#8b949e" }}>{(tea.headlines || []).length} articles</span>
          </div>
          <div style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.6 }}>
            {tea.rationale || `${tea.sentiment === "bullish" ? "Positive news flow" : tea.sentiment === "bearish" ? "Negative news pressure" : "Mixed sentiment"} detected across ${(tea.headlines || []).length} recent headlines. Score ${tea.teaScore}/100.`}
          </div>
        </div>
      )}

      {/* About section */}
      {p.description && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", marginBottom: 10 }}>About {p.shortName || p.longName || ticker}</div>
          <div style={{ fontSize: 14, color: "#c9d1d9", lineHeight: 1.6, marginBottom: 8 }}>
            {bioExpanded ? p.description : (p.description.length > 180 ? p.description.slice(0, 180) + "..." : p.description)}
          </div>
          {p.description.length > 180 && (
            <button onClick={() => setBioExpanded(!bioExpanded)} style={{ background: "none", border: "none", color: "#16a34a", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
              {bioExpanded ? "Show less" : "Show more"}
            </button>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 20px", marginTop: 18 }}>
            {p.ceo && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>CEO</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.ceo}</div></div>)}
            {p.sector && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Sector</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.sector}</div></div>)}
            {p.employees && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Employees</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{fInt(p.employees)}</div></div>)}
            {p.hq && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Headquarters</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.hq}</div></div>)}
            {p.industry && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Industry</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.industry}</div></div>)}
            {p.exchange && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Exchange</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.exchange}</div></div>)}
            {p.ipo && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>IPO Date</div><div style={{ fontSize: 14, color: "#e6edf3", fontWeight: 500 }}>{p.ipo}</div></div>)}
            {p.website && (<div><div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Website</div><a href={p.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#60a5fa", fontWeight: 500, textDecoration: "none" }}>{p.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a></div>)}
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>Stats <span style={{ color: "#636e7b", fontSize: 16 }}>›</span></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Bid</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", lineHeight: 1.1 }}>${fN(bid, 2)}</div>
            {bidSize != null && <div style={{ fontSize: 11, color: "#636e7b", marginTop: 2 }}>x {fInt(bidSize)}</div>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ width: 42, height: 26, borderRadius: 5, background: "rgba(22,163,74,0.08)", border: "1.5px solid rgba(22,163,74,0.35)" }} />
            <div style={{ width: 42, height: 26, borderRadius: 5, background: "rgba(239,68,68,0.06)", border: "1.5px solid rgba(239,68,68,0.3)" }} />
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>Ask</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", lineHeight: 1.1 }}>${fN(ask, 2)}</div>
            {askSize != null && <div style={{ fontSize: 11, color: "#636e7b", marginTop: 2 }}>x {fInt(askSize)}</div>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "18px 12px" }}>
          {[
            ["Volume", volume ? fBig(volume) : "—"],
            ["Average vol", p.averageVolume ? fBig(p.averageVolume) : "—"],
            ["Market cap", p.marketCap ? f$(p.marketCap, 2) : "—"],
            ["Open", p.open != null ? "$" + fN(p.open, 2) : "—"],
            ["Today's high", p.dayHigh != null ? "$" + fN(p.dayHigh, 2) : "—"],
            ["Today's low", p.dayLow != null ? "$" + fN(p.dayLow, 2) : "—"],
            ["52-week high", p.fiftyTwoWeekHigh != null ? "$" + fN(p.fiftyTwoWeekHigh, 2) : "—"],
            ["52-week low", p.fiftyTwoWeekLow != null ? "$" + fN(p.fiftyTwoWeekLow, 2) : "—"],
            ["1Y target", p.oneYrTarget != null ? "$" + fN(p.oneYrTarget, 2) : "—"],
            ["P/E ratio", p.trailingPE != null ? fN(p.trailingPE, 2) : "—"],
            ["EPS (TTM)", p.eps != null ? "$" + fN(p.eps, 2) : "—"],
            ["Beta", p.beta != null ? fN(p.beta, 2) : "—"],
            ["Div yield", p.dividendYield != null ? fPct(p.dividendYield, 2) : "—"],
            ["Annual div", p.annualDividend != null ? "$" + fN(p.annualDividend, 2) : "—"],
            ["Ex-div date", p.exDividendDate || "—"],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: 11, color: "#636e7b", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#e6edf3" }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* News */}
      {news.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", marginBottom: 10 }}>News</div>
          {(newsExpanded ? news : news.slice(0, 3)).map((a, i) => (
            <div key={i} onClick={() => setSelectedNews(a)} style={{ display: "block", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer" }} data-testid={`news-item-${i}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#636e7b", marginBottom: 4 }}>
                {a.source && <span style={{ fontWeight: 700, color: "#8b949e" }}>{a.source}</span>}
                {a.pubDate && <span>· {timeAgo(a.pubDate)}</span>}
                <span style={{ marginLeft: "auto", fontSize: 9, color: "#a78bfa", fontWeight: 700 }}>OMEGA ZI →</span>
              </div>
              <div style={{ fontSize: 14, color: "#e6edf3", lineHeight: 1.4, fontWeight: 500 }}>{a.title}</div>
              <div style={{ fontSize: 11, color: PRIMARY, fontWeight: 600, marginTop: 4 }}>{ticker} {up ? "+" : ""}{fN(changePct, 2)}%</div>
            </div>
          ))}
          {news.length > 3 && (
            <button onClick={() => setNewsExpanded(!newsExpanded)} style={{ background: "none", border: "none", color: "#16a34a", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "14px 0 0", textDecoration: "underline" }}>
              {newsExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}

      {/* News Detail Modal with Omega ZI AI analysis */}
      {selectedNews && (
        <NewsDetailModal article={selectedNews} ticker={ticker} changePct={changePct} onClose={() => setSelectedNews(null)} />
      )}

      {/* Sticky Trade Button */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px calc(24px + env(safe-area-inset-bottom))", background: "linear-gradient(transparent, #0d1117 8%)", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div><div style={{ fontSize: 11, color: "#636e7b" }}>Today's volume</div><div style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3" }}>{fInt(volume)}</div></div>
          <button onClick={() => onTradeOptions(scan)} data-testid="trade-options-btn" style={{ padding: "14px 46px", borderRadius: 30, background: "#16a34a", color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", boxShadow: "0 6px 20px rgba(22,163,74,0.35)" }}>Trade</button>
        </div>
      </div>
    </div>
  );
}

// ═══ STEP 3: OPTION CHAIN ═══════════════════════════════════════════════════
function ChainStep({ ticker, onBack, onSelectContract }) {
  const [chain, setChain] = useState(null);
  const [side, setSide] = useState("calls");
  const [loading, setLoading] = useState(true);
  const [expirations, setExpirations] = useState([]);
  const [expiry, setExpiry] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    // Paint cached expirations instantly
    const cachedExp = getCachedExpirations(ticker);
    if (cachedExp && cachedExp.length) {
      setExpirations(cachedExp);
      if (!expiry) setExpiry(cachedExp[0]);
    }
    axios.get(`${API}/market/expirations?symbol=${ticker}`).then(r => {
      const dates = r.data?.expirations || [];
      setExpirations(dates);
      setCachedExpirations(ticker, dates);
      if (dates.length > 0 && !expiry) setExpiry(dates[0]);
    }).catch(() => {});
    axios.get(`${API}/market/quotes?symbols=${ticker}`).then(r => {
      const q = Array.isArray(r.data) ? r.data[0] : r.data;
      setQuote(q);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  useEffect(() => {
    if (!expiry) return;

    // 1) Instant paint from localStorage chain cache
    const cc = getCachedChain(ticker, expiry);
    if (cc) {
      setChain(cc);
      setLoading(false);
      if (cc.__isFresh) return; // skip revalidate if < 15s old
    } else {
      setLoading(true);
    }

    // 2) Revalidate in background
    axios.get(`${API}/market/chain?symbol=${ticker}&expiry=${expiry}`)
      .then(r => { setChain(r.data); setCachedChain(ticker, expiry, r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticker, expiry]);

  const stockPrice = quote?.price || chain?.stockPrice || 0;
  const contracts = chain?.[side] || [];
  const sorted = [...contracts].sort((a, b) => a.strike - b.strike);
  const atmIdx = sorted.findIndex(c => c.strike >= stockPrice);
  // Show ATM ± 3 strikes (7 visible by default)
  const [showAll, setShowAll] = useState(false);
  const windowed = showAll || atmIdx < 0 ? sorted : sorted.slice(Math.max(0, atmIdx - 3), Math.min(sorted.length, atmIdx + 4));

  return (
    <div style={{ padding: "0 0 32px" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>Option Chain</div>
      <div style={{ fontSize: 13, color: "#636e7b", marginBottom: 14 }}>{ticker} · ${fN(stockPrice, 2)}</div>

      {/* Expiration picker */}
      {expirations.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
          {expirations.slice(0, 10).map(exp => {
            const dte = Math.max(0, Math.ceil((new Date(exp) - new Date()) / 86400000));
            return (
              <button key={exp} onClick={() => setExpiry(exp)} data-testid={`exp-${exp}`} style={{
                flexShrink: 0, padding: "8px 14px", borderRadius: 20, border: "none", cursor: "pointer",
                background: expiry === exp ? "rgba(22,163,74,0.16)" : "rgba(255,255,255,0.03)",
                color: expiry === exp ? "#16a34a" : "#8b949e",
                fontSize: 12, fontWeight: expiry === exp ? 700 : 500,
              }}>{new Date(exp).toLocaleDateString("en-US", { month: "short", day: "numeric" })} <span style={{ opacity: 0.6 }}>· {dte}d</span></button>
            );
          })}
        </div>
      )}

      {/* Calls / Puts toggle */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: 3 }}>
        {["calls", "puts"].map(s => (
          <button key={s} onClick={() => setSide(s)} data-testid={`side-${s}`} style={{
            flex: 1, padding: "11px 0", borderRadius: 10, fontSize: 14, fontWeight: side === s ? 700 : 500,
            background: side === s ? (s === "calls" ? "rgba(22,163,74,0.12)" : "rgba(239,68,68,0.12)") : "transparent",
            color: side === s ? (s === "calls" ? "#16a34a" : "#ef4444") : "#636e7b",
            border: "none", cursor: "pointer", textTransform: "capitalize",
          }}>{s}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#636e7b" }}><span className="spin" /> Loading chain...</div>
      ) : sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#636e7b" }}>No options available</div>
      ) : (
        <div>
          {windowed.map((c, i) => {
            const absIdx = sorted.indexOf(c);
            const isATM = absIdx === atmIdx;
            const itm = side === "calls" ? stockPrice >= c.strike : stockPrice <= c.strike;
            const mid = c.mid || c.last || c.lastPrice || ((c.bid || 0) + (c.ask || 0)) / 2;
            const dte = c.dte != null ? c.dte : (c.expiry ? Math.max(0, Math.ceil((new Date(c.expiry) - new Date()) / 86400000)) : null);
            const oi = c.openInterest ?? c.oi;
            return (
              <div key={c.osi || `${c.strike}-${i}`} onClick={() => onSelectContract({ ...c, side, dte, mid, openInterest: oi, lastPrice: c.last || c.lastPrice, stockPrice, expiry: c.expiry || expiry })} data-testid={`chain-contract-${i}`}
                style={{
                  padding: "14px 16px", borderRadius: 14, marginBottom: 6,
                  background: isATM ? "rgba(96,165,250,0.06)" : "rgba(255,255,255,0.02)",
                  border: isATM ? "1px solid rgba(96,165,250,0.25)" : "1px solid rgba(255,255,255,0.04)",
                  cursor: "pointer",
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 17, fontWeight: 700, color: "#e6edf3" }}>${c.strike}</span>
                      {isATM && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "rgba(96,165,250,0.18)", color: "#60a5fa", fontWeight: 700 }}>ATM</span>}
                      {itm && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "rgba(74,222,128,0.1)", color: "#4ade80", fontWeight: 600 }}>ITM</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#636e7b", marginTop: 2 }}>
                      {dte != null && <span>{dte}d</span>}
                      {(c.iv != null && c.iv > 0) && <span> · IV {fN(c.iv * 100, 0)}%</span>}
                      {c.volume > 0 && <span> · Vol {fInt(c.volume)}</span>}
                      {oi > 0 && <span> · OI {fInt(oi)}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>${fN(mid, 2)}</div>
                    <div style={{ fontSize: 10, color: "#636e7b" }}>${fN(c.bid, 2)} / ${fN(c.ask, 2)}</div>
                  </div>
                </div>
              </div>
            );
          })}
          {!showAll && sorted.length > 7 && (
            <button onClick={() => setShowAll(true)} data-testid="show-all-strikes" style={{ width: "100%", padding: "12px 0", borderRadius: 12, background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.12)", color: "#60a5fa", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 6 }}>
              Show all {sorted.length} strikes
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ═══ P/L AT EXPIRATION CHART ════════════════════════════════════════════════
export function PLChart({ contract, stockPrice, mid, qty, isCall, breakeven }) {
  // Generate P/L curve: for each price point, compute payoff at expiration
  const strike = contract.strike;
  const premium = mid;
  const multiplier = 100 * qty;
  const lo = stockPrice * 0.8;
  const hi = stockPrice * 1.2;
  const points = 40;
  const data = [];
  const labels = [];
  for (let i = 0; i <= points; i++) {
    const price = lo + ((hi - lo) * i) / points;
    // Payoff at expiration
    const intrinsic = isCall ? Math.max(0, price - strike) : Math.max(0, strike - price);
    const pl = (intrinsic - premium) * multiplier;
    data.push(pl);
    labels.push(price);
  }
  // Color segments: green when profit, red when loss
  const curColor = (ctx) => {
    const v = ctx.p1.parsed.y;
    return v >= 0 ? "rgba(74,222,128,0.9)" : "rgba(239,68,68,0.9)";
  };
  const chartData = {
    labels: labels.map(p => "$" + p.toFixed(0)),
    datasets: [
      {
        data,
        borderColor: "rgba(255,255,255,0.3)",
        segment: { borderColor: curColor },
        backgroundColor: (ctx) => {
          const v = ctx.parsed?.y ?? 0;
          return v >= 0 ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)";
        },
        fill: { target: { value: 0 }, above: "rgba(74,222,128,0.12)", below: "rgba(239,68,68,0.10)" },
        pointRadius: 0, borderWidth: 2, tension: 0.05,
      },
    ],
  };
  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(13,17,23,0.95)", callbacks: { title: (ctx) => ctx[0].label, label: (ctx) => `P/L: ${ctx.parsed.y >= 0 ? "+" : ""}$${ctx.parsed.y.toFixed(0)}` } } },
    scales: { x: { display: false }, y: { display: false } },
  };
  const maxLoss = -premium * multiplier;
  const maxProfit = isCall ? "Unlimited" : `$${((strike - premium) * multiplier).toFixed(0)}`;
  return (
    <div style={{ marginBottom: 16, padding: "14px 16px 12px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Profit / Loss at expiration</div>
        <div style={{ fontSize: 10, color: "#636e7b" }}>{qty}× contract{qty > 1 ? "s" : ""}</div>
      </div>
      <div style={{ height: 130, position: "relative" }}>
        <Line data={chartData} options={chartOpts} />
        {/* Breakeven marker */}
        <div style={{ position: "absolute", top: 6, right: 8, fontSize: 9, color: "#fbbf24", background: "rgba(251,191,36,0.12)", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>B/E ${breakeven.toFixed(2)}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10 }}>
        <div><div style={{ fontSize: 9, color: "#636e7b" }}>Max Loss</div><div style={{ fontSize: 13, fontWeight: 700, color: "#f87171" }}>${Math.abs(maxLoss).toFixed(0)}</div></div>
        <div><div style={{ fontSize: 9, color: "#636e7b" }}>Max Profit</div><div style={{ fontSize: 13, fontWeight: 700, color: "#4ade80" }}>{maxProfit}</div></div>
        <div><div style={{ fontSize: 9, color: "#636e7b" }}>Breakeven</div><div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24" }}>${breakeven.toFixed(2)}</div></div>
      </div>
    </div>
  );
}

// ═══ PROBABILITY CONE — current price / strike / MC target against the 95% CI ══
export function ProjectionCone({ current, strike, target, ci95 }) {
  const lo95 = ci95?.[0] ?? current;
  const hi95 = ci95?.[1] ?? current;
  const lo = Math.min(lo95, current, strike) * 0.98;
  const hi = Math.max(hi95, current, strike) * 1.02;
  const span = Math.max(hi - lo, 0.01);
  const pct = (v) => Math.min(100, Math.max(0, ((v - lo) / span) * 100));
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ position: "relative", height: 8, borderRadius: 4, background: "rgba(255,255,255,0.06)" }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, borderRadius: 4, background: "rgba(74,222,128,0.28)", left: `${pct(lo95)}%`, width: `${Math.max(0, pct(hi95) - pct(lo95))}%` }} />
        <div style={{ position: "absolute", top: -4, width: 2, height: 16, borderRadius: 1, background: "#fff", left: `${pct(current)}%` }} title="Current price" />
        <div style={{ position: "absolute", top: -4, width: 2, height: 16, borderRadius: 1, background: "#fbbf24", left: `${pct(strike)}%` }} title="Strike" />
        <div style={{ position: "absolute", top: -4, width: 2, height: 16, borderRadius: 1, background: "#4ade80", left: `${pct(target)}%` }} title="Zi target" />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8.5, color: "#636e7b", marginTop: 4 }}>
        <span>${lo.toFixed(0)}</span>
        <span>white now · gold strike · green target</span>
        <span>${hi.toFixed(0)}</span>
      </div>
    </div>
  );
}

// ═══ OMEGA ZI PROJECTION PANEL — real Monte Carlo terminal-price simulation
// off the contract's own IV (engine.py monte_carlo_gbm), plus a real
// regime/drift read. Pulled out as its own component so QuickTradeModal can
// show the same panel a fresh buy already gets, on an existing position. ═══
export function ZiProjectionPanel({ projection, stockPrice, strike }) {
  if (!projection) return null;
  return (
    <div style={{ marginBottom: 16, padding: 14, borderRadius: 14, background: "linear-gradient(135deg, rgba(74,222,128,0.05), rgba(96,165,250,0.03))", border: "1px solid rgba(74,222,128,0.14)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80" }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", letterSpacing: 0.5 }}>OMEGA ZI PROJECTION</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: "#04210f", background: "#4ade80", padding: "3px 9px", borderRadius: 999 }}>{Math.round(projection.probProfit * 100)}% PoP</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: "#636e7b" }}>Target by expiration</span>
        <span style={{ fontSize: 19, fontWeight: 800, color: "#e6edf3" }}>${fN(projection.priceTarget, 2)}</span>
      </div>
      <div style={{ fontSize: 10.5, color: "#636e7b", marginBottom: 12 }}>60th-pctile GBM path · 95% range ${fN(projection.ci95?.[0], 2)} – ${fN(projection.ci95?.[1], 2)}</div>

      <ProjectionCone current={stockPrice} strike={strike} target={projection.priceTarget} ci95={projection.ci95} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingTop: 12, marginTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, padding: "4px 8px", borderRadius: 7, background: "rgba(255,255,255,0.05)", color: "#8b949e" }}>IV <b style={{ color: "#e6edf3" }}>{fN(projection.iv, 0)}%</b></span>
        {projection.regime && (
          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "4px 8px", borderRadius: 7, background: "rgba(255,255,255,0.05)", color: "#8b949e" }}>
            Regime <b style={{ color: "#e6edf3" }}>{projection.regime.replace(/_/g, " ")} {projection.regimeAnnualReturnPct >= 0 ? "+" : ""}{fN(projection.regimeAnnualReturnPct, 0)}%a</b>
          </span>
        )}
        <span style={{ fontSize: 9.5, fontWeight: 700, padding: "4px 8px", borderRadius: 7, background: "rgba(255,255,255,0.05)", color: "#8b949e" }}>EV <b style={{ color: projection.expectedValue >= 0 ? "#4ade80" : "#f87171" }}>{projection.expectedValue >= 0 ? "+" : ""}{fN(projection.expectedValue, 0)}%</b></span>
      </div>
    </div>
  );
}

// ═══ STEP 4: CONTRACT DETAIL + EXECUTION ════════════════════════════════════
function ExecuteStep({ ticker, contract, onBack, onResult }) {
  const [qty, setQty] = useState(1);
  const [greeks, setGreeks] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [insight, setInsight] = useState("");
  const [liveMid, setLiveMid] = useState(null);
  const [stockPrice, setStockPrice] = useState(contract.stockPrice || 0);
  const [projection, setProjection] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [dryPreview, setDryPreview] = useState(null);
  // iter 96 — ExecutionConfirmModal handshake
  const [confirmOrder, setConfirmOrder] = useState(null);
  // Real USD buying power + real zBucks balance, so the funding toggle shows
  // actual numbers instead of a bare label — /order/place already accepts
  // fundingSource:"zbucks"|"usd" (ExecutionConfirmModal has supported it for
  // a while), this was just never exposed as a choice in the mobile flow.
  const [fundingSource, setFundingSource] = useState("usd");
  const [usdBuyingPower, setUsdBuyingPower] = useState(null);
  const [zbucksBalance, setZbucksBalance] = useState(null);
  useEffect(() => {
    axios.get(`${API}/portfolio`).then(r => setUsdBuyingPower(r.data?.optionsBuyingPower ?? r.data?.buyingPower ?? null)).catch(() => {});
    axios.get(`${API}/zbucks/user/summary`).then(r => setZbucksBalance(r.data?.balance ?? null)).catch(() => {});
  }, []);

  const osi = contract.osi || contract.symbol || "";
  const mid = liveMid ?? (contract.mid || contract.last || contract.lastPrice || ((contract.bid || 0) + (contract.ask || 0)) / 2);
  const totalCost = mid * qty * 100;
  const sideLabel = contract.side === "puts" ? "Put" : "Call";
  const isCall = contract.side !== "puts";
  const dte = contract.dte != null ? contract.dte : (contract.expiry ? Math.max(0, Math.ceil((new Date(contract.expiry) - new Date()) / 86400000)) : null);
  const breakeven = isCall ? (contract.strike + mid) : (contract.strike - mid);
  const moneyness = stockPrice && contract.strike ? ((stockPrice - contract.strike) / contract.strike * 100) : 0;
  const itm = isCall ? stockPrice > contract.strike : stockPrice < contract.strike;
  const moneyLabel = Math.abs(moneyness) < 1 ? "ATM" : itm ? "ITM" : "OTM";

  useEffect(() => {
    if (!osi) return;
    // ExecuteStep isn't remounted between contracts (no `key` at the call
    // site), so a stale projection from the previous contract would
    // otherwise survive — clear it so the guarded fetch effect below
    // re-runs for the new contract instead of skipping forever.
    setProjection(null);
    axios.get(`${API}/market/greeks?osi=${osi}`).then(r => setGreeks(r.data || null)).catch(() => {});
    axios.get(`${API}/market/quotes?symbols=${ticker}`).then(r => {
      const q = Array.isArray(r.data) ? r.data[0] : r.data;
      if (q?.price) setStockPrice(q.price);
    }).catch(() => {});
    const prompt = `Option: ${ticker} $${contract.strike} ${sideLabel}, Mid: $${mid.toFixed(2)}, DTE: ${dte != null ? dte : "?"}, IV: ${contract.iv ? (contract.iv*100).toFixed(0)+"%" : "?"}, Moneyness: ${moneyLabel} (${moneyness.toFixed(1)}%). In 2 concise sentences: Is this a good entry? What's the main risk?`;
    axios.post(`${API}/zi/chat`, { message: prompt, sessionId: `exec-${Date.now()}` }).then(r => setInsight(r.data?.response || "")).catch(() => {
      setInsight(`${ticker} $${contract.strike} ${sideLabel} at $${mid.toFixed(2)}. ${dte != null && dte <= 7 ? "Short DTE — theta decay is sharp." : "Reasonable time horizon."} ${contract.iv > 0.4 ? "Elevated IV — premium is richer than normal." : "IV is within range."}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osi]);

  // Poll live mid price
  useEffect(() => {
    if (!osi) return;
    const poll = () => axios.get(`${API}/market/greeks?osi=${osi}`).then(r => {
      if (r.data?.mid != null) setLiveMid(r.data.mid);
      else if (r.data?.bid != null && r.data?.ask != null) setLiveMid((r.data.bid + r.data.ask) / 2);
    }).catch(() => {});
    const iv = setInterval(poll, 4000);
    return () => clearInterval(iv);
  }, [osi]);

  // Omega Zi Projection — single-contract Monte Carlo run off the real IV
  // once greeks land; fires once per contract, guarded by the `projection`
  // dep so a later greeks/mid refresh doesn't keep re-firing it.
  useEffect(() => {
    // greeks?.iv can legitimately come back as 0 (not just missing) for a
    // 0-DTE contract right near expiry, where IV is numerically unstable —
    // guard on it being a usable positive number, not just non-null, or
    // this fires a doomed request (backend correctly rejects sigma=0).
    if (projection || !ticker || !contract.strike || !stockPrice || !mid || !(greeks?.iv > 0)) return;
    axios.post(`${API}/options/projection`, {
      ticker, strike: contract.strike, stockPrice, iv: greeks.iv, mid, dte: dte ?? 1,
      optionType: isCall ? "call" : "put",
    }).then(r => setProjection(r.data || null)).catch(() => {});
  }, [projection, ticker, contract.strike, stockPrice, mid, greeks, dte, isCall]);

  const placeOrder = async () => {
    hapticTap();
    setDryPreview(null);

    // Refresh live option mid right before opening the confirm modal —
    // prevents stale-quote rejects after the user has been staring at the
    // page for a while.
    let freshMid = mid;
    try {
      const q = await axios.get(`${API}/market/option-quote?osi=${encodeURIComponent(osi)}`);
      const m = q.data?.mid || q.data?.last;
      if (m && m > 0) freshMid = m;
    } catch (_) { /* keep last */ }

    const body = {
      symbol: osi, osi, instrumentType: "OPTION",
      side: "BUY", orderType: "LIMIT", quantity: qty,
      limitPrice: freshMid, openClose: "OPEN",
      fundingSource,
      // Real contract details so a zbucks fill stores a readable position
      // (ticker/strike/optionType/expiry) instead of just the bare OSI —
      // /order/place already accepts these, the request just never sent them.
      ticker, strike: contract.strike, optionType: isCall ? "call" : "put",
      expiry: contract.expiry || contract.expDate,
    };

    // Dry-run is a broker preflight — doesn't apply to zbucks paper fills
    // (they never touch the broker), so it's hidden whenever zbucks is
    // selected; this only fires for real USD orders.
    if (dryRun && fundingSource === "usd") {
      setPlacing(true);
      try {
        const pf = await axios.post(`${API}/order/preflight`, { ...body, dryRun: true });
        if (pf.data?.ok) { hapticSuccess(); setDryPreview(pf.data?.preflight || {}); }
        else { hapticError(); onResult({ success: false, error: pf.data?.error || "Preflight failed" }); }
      } catch (e) {
        hapticError();
        onResult({ success: false, error: e.response?.data?.detail || e.message || "Preflight failed" });
      }
      setPlacing(false);
      return;
    }

    // Real execution goes through the swipe-to-confirm modal.
    setConfirmOrder({
      ...body,
      label: `${ticker} · $${contract.strike} ${sideLabel}${fundingSource === "zbucks" ? " · zBucks" : ""}`,
    });
  };

  const onExecConfirmed = (result) => {
    hapticSuccess();
    onResult({ success: true, orderId: result?.order?.orderId || result?.orderId || result?.order_id || result?.id, ...result });
  };

  return (
    <div style={{ padding: "0 0 240px" }} data-testid="execute-step">
      {/* Contract header */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#636e7b" }}>{ticker}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>${contract.strike} {sideLabel}</div>
        <div style={{ fontSize: 13, color: "#636e7b", marginTop: 4 }}>Exp {contract.expiry || contract.expDate || "—"} · {dte != null ? dte + " DTE" : "— DTE"}</div>
      </div>

      {/* Live mid price card */}
      <div style={{ textAlign: "center", padding: "18px 0", marginBottom: 16, borderRadius: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Mid Price</div>
        <div style={{ fontSize: 38, fontWeight: 800, color: "#e6edf3", lineHeight: 1.1, marginTop: 4 }}>${fN(mid, 2)}</div>
        <div style={{ fontSize: 12, color: "#636e7b", marginTop: 2 }}>Bid ${fN(contract.bid, 2)} · Ask ${fN(contract.ask, 2)}</div>
      </div>

      {/* Moneyness meter */}
      {stockPrice > 0 && (
        <div style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Moneyness</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: moneyLabel === "ITM" ? "#4ade80" : moneyLabel === "OTM" ? "#f87171" : "#60a5fa" }}>{moneyLabel}</span>
              <span style={{ fontSize: 11, color: "#636e7b" }}>{moneyness >= 0 ? "+" : ""}{fN(moneyness, 1)}%</span>
            </div>
          </div>
          {/* Bar */}
          <div style={{ position: "relative", height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "visible" }}>
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(239,68,68,0.2) 0%, rgba(251,191,36,0.2) 50%, rgba(74,222,128,0.2) 100%)", borderRadius: 3 }} />
            <div style={{ position: "absolute", left: `${Math.min(100, Math.max(0, 50 + moneyness * (isCall ? 1 : -1) * 2))}%`, top: -4, width: 3, height: 14, background: "#fff", borderRadius: 2, transform: "translateX(-50%)" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: "#636e7b" }}>
            <span>Deep OTM</span><span>ATM</span><span>Deep ITM</span>
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8b949e" }}>
            <span>Stock: <strong style={{ color: "#e6edf3" }}>${fN(stockPrice, 2)}</strong></span>
            <span>Strike: <strong style={{ color: "#e6edf3" }}>${contract.strike}</strong></span>
            <span>B/E: <strong style={{ color: "#fbbf24" }}>${fN(breakeven, 2)}</strong></span>
          </div>
        </div>
      )}

      {/* P/L at Expiration chart */}
      {stockPrice > 0 && mid > 0 && (
        <PLChart contract={contract} stockPrice={stockPrice} mid={mid} qty={qty} isCall={isCall} breakeven={breakeven} />
      )}

      {/* Quantity stepper */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28, marginBottom: 16 }}>
        <button onClick={() => { hapticTap(); setQty(q => Math.max(1, q - 1)); }} data-testid="qty-minus" style={{ width: 44, height: 44, borderRadius: 22, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: "pointer", fontWeight: 600 }}>−</button>
        <div style={{ textAlign: "center" }}><div style={{ fontSize: 11, color: "#636e7b" }}>Contracts</div><div style={{ fontSize: 30, fontWeight: 800, color: "#e6edf3" }}>{qty}</div></div>
        <button onClick={() => { hapticTap(); setQty(q => q + 1); }} data-testid="qty-plus" style={{ width: 44, height: 44, borderRadius: 22, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: "pointer", fontWeight: 600 }}>+</button>
      </div>
      <div style={{ textAlign: "center", fontSize: 14, color: "#636e7b", marginBottom: 20 }}>Total: <strong style={{ color: "#e6edf3", fontSize: 18 }}>${fN(totalCost, 2)}</strong></div>

      {/* Greeks */}
      {greeks && Object.keys(greeks).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Greeks</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[["Delta", greeks.delta, "#60a5fa", 3], ["Theta", greeks.theta, "#f87171", 4], ["Gamma", greeks.gamma, "#8b949e", 4], ["Vega", greeks.vega, "#8b949e", 4], ["IV", greeks.iv != null ? greeks.iv * 100 : null, "#fbbf24", 0]].filter(([, v]) => v != null).map(([label, val, color, d]) => (
              <div key={label} style={{ padding: "8px 10px", borderRadius: 12, background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 10, color: "#636e7b" }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color }}>{label === "IV" ? fN(val, d) + "%" : fN(val, d)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Omega Zi Projection — replaces the old "Omega Option Info" card,
          which read scan.ziScore/scan.topPick — fields /zi/scan has never
          actually returned, so that card never rendered a single value in
          production. Component itself lives above ExecuteStep, shared with
          QuickTradeModal. */}
      <ZiProjectionPanel projection={projection} stockPrice={stockPrice} strike={contract.strike} />

      {/* Omega AI Prediction */}
      {insight && (
        <div style={{ padding: 14, borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.12)", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 0.5 }}>OMEGA ZI AI PREDICTION</span>
          </div>
          <div style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.6 }}>{insight}</div>
        </div>
      )}

      {/* Buy Button sticky */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px calc(24px + env(safe-area-inset-bottom))", background: "linear-gradient(transparent, #0d1117 8%)", zIndex: 10 }}>
        {dryPreview && (
          <div data-testid="otf-dry-preview" style={{ padding: 10, borderRadius: 10, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.24)", marginBottom: 10, fontSize: 12, color: "#93c5fd" }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: "#60a5fa" }}>✓ Dry Run Preflight</div>
            <div>Order value: <strong>${Number(dryPreview.order_value ?? 0).toFixed(2)}</strong>
              {dryPreview.estimated_commission != null && <> · Commission: <strong>${Number(dryPreview.estimated_commission).toFixed(2)}</strong></>}
              {dryPreview.buying_power_requirement != null && <> · BP: <strong>${Number(dryPreview.buying_power_requirement).toFixed(2)}</strong></>}
            </div>
          </div>
        )}

        {/* Funding source — real USD buying power vs real zBucks balance */}
        <div data-testid="otf-funding-toggle" style={{ display: "flex", gap: 6, padding: 4, borderRadius: 14, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", marginBottom: 10 }}>
          {[
            { key: "usd", label: "USD", sub: usdBuyingPower != null ? `$${fN(usdBuyingPower, 0)} BP` : "—", color: "#4ade80" },
            { key: "zbucks", label: "zBucks", sub: zbucksBalance != null ? `Ⓩ${fN(zbucksBalance, 0)}` : "—", color: "#fbbf24" },
          ].map(opt => (
            <button key={opt.key} onClick={() => { hapticTap(); setFundingSource(opt.key); if (opt.key === "zbucks") { setDryRun(false); setDryPreview(null); } }}
              data-testid={`otf-fund-${opt.key}`}
              style={{
                flex: 1, padding: "9px 10px", borderRadius: 10, border: "none", cursor: "pointer",
                background: fundingSource === opt.key ? `${opt.color}22` : "transparent",
                color: fundingSource === opt.key ? opt.color : "#8b949e",
              }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{opt.label}</div>
              <div style={{ fontSize: 9.5, marginTop: 1, opacity: 0.85 }}>{opt.sub}</div>
            </button>
          ))}
        </div>

        {fundingSource === "usd" && (
          <label data-testid="otf-dry-run-toggle" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 11, color: "#93c5fd", marginBottom: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => { setDryRun(e.target.checked); setDryPreview(null); }} style={{ accentColor: "#60a5fa", cursor: "pointer" }} />
            Dry Run · preflight-only (no order placed)
          </label>
        )}
        <button onClick={placeOrder} disabled={placing} data-testid="buy-now-btn" style={{
          width: "100%", padding: "16px", borderRadius: 30,
          background: placing ? "#636e7b" : (dryRun ? "#60a5fa" : fundingSource === "zbucks" ? "#f59e0b" : "#16a34a"),
          color: "#fff", fontSize: 16, fontWeight: 700, border: "none",
          cursor: placing ? "default" : "pointer",
          boxShadow: placing ? "none" : (dryRun ? "0 6px 20px rgba(96,165,250,0.35)" : fundingSource === "zbucks" ? "0 6px 20px rgba(245,158,11,0.35)" : "0 6px 20px rgba(22,163,74,0.35)"),
        }}>{placing ? "Placing order..." : `${dryRun ? "Dry Run · " : "Buy "}${qty} · ${fundingSource === "zbucks" ? "Ⓩ" : "$"}${fN(totalCost, 2)}`}</button>
      </div>

      {confirmOrder && (
        <ExecutionConfirmModal
          order={confirmOrder}
          onClose={() => setConfirmOrder(null)}
          onConfirmed={(r) => { setConfirmOrder(null); onExecConfirmed(r); }}
        />
      )}
    </div>
  );
}

// ═══ STEP 5: RESULT ═════════════════════════════════════════════════════════
function ResultStep({ result, ticker, onBackToSymbol }) {
  const [sharing, setSharing] = useState(false);

  const buildShareText = () => {
    if (!result?.success) return "";
    if (result.combo) {
      return [
        `🚀 Just placed a ${result.strategy?.name || "combo"} on Omega ZI AI`,
        `${ticker} · ${result.legs?.length || 0} legs · ${result.isCredit ? "Credit" : "Debit"} $${fN(Math.abs(result.netCost), 2)}`,
        `Powered by Omega Zi Score engine`,
      ].filter(Boolean).join("\n");
    }
    const lines = [
      `🚀 Just placed an options trade on Omega ZI AI`,
      `${ticker} · ${result.contract?.strike ? `$${result.contract.strike} ${result.contract.side === "puts" ? "Put" : "Call"}` : "Order"}`,
      result.orderId ? `Order: ${result.orderId}` : "",
      `Powered by Omega Zi Score engine`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const handleShare = async () => {
    hapticTap();
    const text = buildShareText();
    const shareData = {
      title: `Omega ZI AI · ${ticker}`,
      text,
      url: window.location.origin,
    };
    try {
      if (navigator.share) {
        setSharing(true);
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}\n${window.location.origin}`);
        alert("Copied to clipboard!");
      }
    } catch (e) {
      // User cancelled or share failed; fallback to clipboard
      try {
        await navigator.clipboard.writeText(`${text}\n${window.location.origin}`);
      } catch {}
    } finally {
      setSharing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center", padding: "0 20px" }} data-testid="result-step">
      {result.success ? (
        <>
          <div style={{ width: 68, height: 68, borderRadius: 34, background: "rgba(22,163,74,0.14)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#e6edf3", marginBottom: 8 }}>Order Submitted</div>
          <div style={{ fontSize: 14, color: "#8b949e", marginBottom: 4 }}>Your order has been sent to the market</div>
          {result.orderId && <div style={{ fontSize: 11, color: "#636e7b", marginTop: 4 }}>Order ID: {result.orderId}</div>}

          {/* Shareable Trade Card */}
          <div style={{ marginTop: 28, width: "100%", maxWidth: 360, padding: 20, borderRadius: 18, background: "linear-gradient(135deg, rgba(74,222,128,0.12), rgba(167,139,250,0.08))", border: "1px solid rgba(74,222,128,0.22)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#4ade80", letterSpacing: 1 }}>✓ {result.combo ? "COMBO FILLED" : "TRADE FILLED"}</div>
              <div style={{ fontSize: 9, color: "#8b949e" }}>OMEGA ZI AI · v6.0</div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>{ticker}</div>
            <div style={{ fontSize: 14, color: "#c9d1d9", marginBottom: 14 }}>
              {result.combo ? (result.strategy?.name || "Multi-leg combo") : (result.symbol || result.osi ? (result.symbol || result.osi) : "Option Trade")}
            </div>
            {result.combo ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div><div style={{ fontSize: 9, color: "#636e7b" }}>Legs</div><div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>{result.legs?.length || 0}</div></div>
                <div><div style={{ fontSize: 9, color: "#636e7b" }}>Net {result.isCredit ? "Credit" : "Debit"}</div><div style={{ fontSize: 16, fontWeight: 700, color: result.isCredit ? "#4ade80" : "#fbbf24" }}>${fN(Math.abs(result.netCost), 2)}</div></div>
              </div>
            ) : (result.quantity || result.qty) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div><div style={{ fontSize: 9, color: "#636e7b" }}>Qty</div><div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>{result.quantity || result.qty}</div></div>
                {(result.limitPrice || result.price) && <div><div style={{ fontSize: 9, color: "#636e7b" }}>Limit</div><div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3" }}>${fN(result.limitPrice || result.price, 2)}</div></div>}
              </div>
            )}
            <div style={{ fontSize: 10, color: "#8b949e", marginTop: 8, lineHeight: 1.5 }}>101-strategy library · Omega Zi Score engine</div>
          </div>

          <button onClick={handleShare} data-testid="share-trade-btn" disabled={sharing} style={{ marginTop: 18, padding: "12px 32px", borderRadius: 24, background: "#16a34a", color: "#fff", fontSize: 14, fontWeight: 700, border: "none", cursor: sharing ? "default" : "pointer", boxShadow: "0 4px 14px rgba(22,163,74,0.3)", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            {sharing ? "Sharing..." : "Share Trade"}
          </button>
        </>
      ) : (
        <>
          <div style={{ width: 68, height: 68, borderRadius: 34, background: "rgba(239,68,68,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#e6edf3", marginBottom: 8 }}>Order Failed</div>
          <div style={{ fontSize: 14, color: "#f87171", marginBottom: 4, maxWidth: 320, lineHeight: 1.5 }}>{result.error || "Something went wrong"}</div>
        </>
      )}
      <button onClick={onBackToSymbol} data-testid="back-to-symbol" style={{ marginTop: 26, padding: "14px 40px", borderRadius: 30, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.22)", color: "#60a5fa", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Back to {ticker}</button>
    </div>
  );
}

// ═══ STRATEGY PICKER STEP ════════════════════════════════════════════════════
const STRATEGY_FILTERS = [
  { id: "single_leg", label: "Single Leg", icon: "●" },
  { id: "vertical", label: "Verticals", icon: "⫽" },
  { id: "iron_condor", label: "Iron Condors", icon: "◇" },
  { id: "butterfly", label: "Butterflies", icon: "▲" },
  { id: "straddle", label: "Straddles", icon: "║" },
  { id: "strangle", label: "Strangles", icon: "‖" },
  { id: "calendar", label: "Calendars", icon: "⧖" },
  { id: "diagonal", label: "Diagonals", icon: "╱" },
];

function StrategyPickerStep({ ticker, onSelectStrategy, onBack, scan }) {
  const [filter, setFilter] = useState("all");
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autonomy, setAutonomy] = useState("omega");

  useEffect(() => {
    axios.get(`${API}/omega/autonomy`).then(r => setAutonomy((r.data?.current || "omega"))).catch(() => {});
  }, []);

  useEffect(() => {
    const regime = scan?.regime || "bull_low_vol";
    setLoading(true);
    axios.get(`${API}/omega/strategies?regime=${regime}&autonomy=${autonomy}`)
      .then(r => setStrategies(r.data?.strategies || []))
      .catch(() => setStrategies([]))
      .finally(() => setLoading(false));
  }, [autonomy, scan]);

  const filtered = filter === "all" ? strategies : strategies.filter(s => s.type === filter);
  const groups = {};
  filtered.forEach(s => {
    if (!groups[s.type]) groups[s.type] = [];
    groups[s.type].push(s);
  });

  const dirColor = (dir) => ({ bullish: "#4ade80", bearish: "#ef4444", neutral: "#60a5fa", volatile: "#f59e0b", directional: "#a78bfa" })[dir] || "#8b949e";

  return (
    <div style={{ padding: "0 0 32px" }} data-testid="strategy-step">
      <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>Choose Strategy</div>
      <div style={{ fontSize: 13, color: "#636e7b", marginBottom: 8 }}>{ticker} · {filtered.length} plays match regime <strong style={{ color: "#a78bfa" }}>{(scan?.regime || "bull_low_vol").replace("_", " ")}</strong></div>
      <div style={{ fontSize: 11, color: "#636e7b", marginBottom: 14 }}>Autonomy: <strong style={{ color: autonomy === "omega" ? "#a78bfa" : "#60a5fa", textTransform: "uppercase" }}>{autonomy}</strong> · Full library: {strategies.length} strategies</div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        <button onClick={() => setFilter("all")} data-testid="strat-filter-all" style={{
          flexShrink: 0, padding: "8px 14px", borderRadius: 20, border: "none", cursor: "pointer",
          background: filter === "all" ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.03)",
          color: filter === "all" ? "#a78bfa" : "#8b949e",
          fontSize: 12, fontWeight: filter === "all" ? 700 : 500,
        }}>All ({strategies.length})</button>
        {STRATEGY_FILTERS.map(f => {
          const count = strategies.filter(s => s.type === f.id).length;
          if (!count) return null;
          return (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`strat-filter-${f.id}`} style={{
              flexShrink: 0, padding: "8px 14px", borderRadius: 20, border: "none", cursor: "pointer",
              background: filter === f.id ? "rgba(96,165,250,0.16)" : "rgba(255,255,255,0.03)",
              color: filter === f.id ? "#60a5fa" : "#8b949e",
              fontSize: 12, fontWeight: filter === f.id ? 700 : 500, display: "flex", alignItems: "center", gap: 5,
            }}><span>{f.icon}</span>{f.label} ({count})</button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#636e7b" }}><span className="spin" /> Loading strategies…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#636e7b", fontSize: 13 }}>No strategies match this filter for the current regime.</div>
      ) : (
        <div>
          {filtered.map((s, i) => (
            <div key={s.id} onClick={() => { hapticTap(); onSelectStrategy(s); }} data-testid={`strategy-${s.id}`} style={{
              padding: "12px 14px", borderRadius: 12, marginBottom: 6,
              background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)",
              cursor: "pointer",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "rgba(167,139,250,0.1)", color: "#a78bfa", fontWeight: 700 }}>#{s.id}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>{s.name}</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#8b949e", display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: dirColor(s.direction), fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.direction}</span>
                    <span>· {s.dte_range[0]}–{s.dte_range[1]} DTE</span>
                    <span>· {s.max_risk} risk</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#c9d1d9", lineHeight: 1.5, marginTop: 6 }}>{s.description}</div>
                </div>
                <div style={{ color: "#60a5fa", fontSize: 18, flexShrink: 0 }}>›</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ STRATEGY SUMMARY / MULTI-LEG AUTO-BUILDER ═══════════════════════════════
function StrategySummary({ strategy, ticker, onContinue, onBack, onPlaceMulti }) {
  const isSingleLeg = strategy.type === "single_leg";
  useEffect(() => { if (isSingleLeg) onContinue("chain"); /* auto-continue */ }, [isSingleLeg, onContinue]);

  const [build, setBuild] = useState(null);
  const [loading, setLoading] = useState(!isSingleLeg);
  const [err, setErr] = useState("");
  const [expiry, setExpiry] = useState("");
  const [expiries, setExpiries] = useState([]);

  // Load expirations for multi-leg
  useEffect(() => {
    if (isSingleLeg) return;
    axios.get(`${API}/market/expirations?symbol=${ticker}`).then(r => {
      const dates = r.data?.expirations || [];
      setExpiries(dates);
      // Pick expiry in DTE range if possible
      const today = new Date();
      const [minDte, maxDte] = strategy.dte_range || [14, 45];
      let pick = dates.find(d => {
        const diff = Math.ceil((new Date(d) - today) / 86400000);
        return diff >= minDte && diff <= maxDte;
      }) || dates[0];
      setExpiry(pick);
    }).catch(() => setErr("Could not load expirations"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, isSingleLeg]);

  // Auto-build when expiry picked
  useEffect(() => {
    if (isSingleLeg || !expiry) return;
    setLoading(true);
    setErr("");
    setBuild(null);
    axios.post(`${API}/strategy/auto-build`, { strategy_id: strategy.id, symbol: ticker, expiry })
      .then(r => {
        if (r.data?.ok === false) setErr(r.data.reason || "Auto-build failed");
        else setBuild(r.data);
      })
      .catch(e => setErr(e.response?.data?.detail || e.message || "Auto-build failed"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiry, strategy.id, ticker, isSingleLeg]);

  if (isSingleLeg) {
    return <div style={{ padding: 40, textAlign: "center", color: "#636e7b" }}><span className="spin" /> Opening chain…</div>;
  }

  const sideColor = (s) => s === "SELL" ? "#f87171" : "#4ade80";
  const isCredit = build && build.netCostPerShare < 0;

  return (
    <div style={{ padding: "0 0 120px" }} data-testid="strategy-summary">
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>{strategy.type.replace("_", " ")}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>{strategy.name}</div>
        <div style={{ fontSize: 12, color: "#8b949e", marginTop: 6, lineHeight: 1.5 }}>{strategy.description}</div>
      </div>

      {/* Expiry picker */}
      {expiries.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: "#636e7b", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Expiration</div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {expiries.slice(0, 10).map(d => {
              const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
              const active = d === expiry;
              return (
                <button key={d} data-testid={`exp-${d}`} onClick={() => { hapticTap(); setExpiry(d); }} style={{
                  flexShrink: 0, padding: "8px 12px", borderRadius: 10, border: "none", cursor: "pointer",
                  background: active ? "rgba(96,165,250,0.18)" : "rgba(255,255,255,0.03)",
                  color: active ? "#60a5fa" : "#8b949e", fontSize: 11, fontWeight: active ? 700 : 500,
                }}>
                  <div>{d}</div>
                  <div style={{ fontSize: 9, opacity: 0.75 }}>{diff}d</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Build state */}
      {loading && (
        <div style={{ padding: 30, textAlign: "center", color: "#636e7b" }} data-testid="autobuild-loading"><span className="spin" /> Auto-building optimal legs…</div>
      )}

      {err && (
        <div style={{ padding: 14, borderRadius: 12, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.22)", marginBottom: 14 }} data-testid="autobuild-error">
          <div style={{ fontSize: 11, color: "#f87171", fontWeight: 700, marginBottom: 4 }}>Auto-build unavailable</div>
          <div style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.5 }}>{err}</div>
        </div>
      )}

      {build && build.legs && build.legs.length > 0 && (
        <>
          {/* Summary */}
          <div style={{ marginBottom: 14, padding: 14, borderRadius: 14, background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 0.5 }}>OMEGA AUTO-BUILT · {build.legs.length} LEGS</div>
              <div style={{ fontSize: 10, color: "#8b949e" }}>Stock ${fN(build.stockPrice, 2)}</div>
            </div>
            {build.legs.map((lg, i) => (
              <div key={i} data-testid={`leg-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < build.legs.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: sideColor(lg.side), width: 38, textAlign: "center", padding: "3px 0", borderRadius: 4, background: `${sideColor(lg.side)}15` }}>{lg.side}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#8b949e", width: 14 }}>{lg.qty}x</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>${lg.strike} {lg.optionType === "put" ? "Put" : "Call"}</div>
                  <div style={{ fontSize: 9, color: "#636e7b", fontFamily: "monospace" }}>{lg.osi}</div>
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: "#c9d1d9", fontWeight: 600 }}>${fN(lg.mid, 2)}</div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(167,139,250,0.22)" }}>
              <span style={{ fontSize: 11, color: "#8b949e" }}>Net {isCredit ? "Credit" : "Debit"} · per contract</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: isCredit ? "#4ade80" : "#fbbf24" }}>{isCredit ? "+" : ""}${fN(Math.abs(build.netCost), 2)}</span>
            </div>
          </div>

          {build.partial && (
            <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.18)" }}>
              <div style={{ fontSize: 10, color: "#fbbf24", fontWeight: 700, marginBottom: 4 }}>PARTIAL BUILD</div>
              <div style={{ fontSize: 11, color: "#c9d1d9", lineHeight: 1.5 }}>{build.reason}</div>
            </div>
          )}

          <button onClick={() => { hapticTap(); onPlaceMulti(build); }} data-testid="place-multi-btn" style={{
            width: "100%", padding: 14, borderRadius: 12, background: "#16a34a", border: "none",
            color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10,
          }}>Review & Place Combo →</button>
          <button onClick={() => { hapticTap(); onContinue("chain"); }} data-testid="continue-to-chain" style={{
            width: "100%", padding: 12, borderRadius: 12, background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.22)",
            color: "#60a5fa", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Or open chain to build manually</button>
        </>
      )}
    </div>
  );
}

// ═══ MULTI-LEG EXECUTE STEP ═════════════════════════════════════════════════
function MultiLegExecuteStep({ build, ticker, strategy, onBack, onResult }) {
  const [qty, setQty] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [progress, setProgress] = useState([]);
  // iter 96 — MultiLegExecutionConfirmModal handshake.
  const [confirmLegs, setConfirmLegs] = useState(null);
  const isCredit = build.netCostPerShare < 0;
  const totalCost = Math.abs(build.netCost) * qty;
  const [fundingSource, setFundingSource] = useState("usd");
  const [usdBuyingPower, setUsdBuyingPower] = useState(null);
  const [zbucksBalance, setZbucksBalance] = useState(null);
  useEffect(() => {
    axios.get(`${API}/portfolio`).then(r => setUsdBuyingPower(r.data?.optionsBuyingPower ?? r.data?.buyingPower ?? null)).catch(() => {});
    axios.get(`${API}/zbucks/user/summary`).then(r => setZbucksBalance(r.data?.balance ?? null)).catch(() => {});
  }, []);

  const placeCombo = () => {
    hapticTap();
    // Pre-build the leg payloads so the ExecutionConfirmModal can fan out
    // /order/preview for each leg in parallel.
    const legs = build.legs.map(lg => ({
      symbol: lg.osi,
      osi: lg.osi,
      side: lg.side,
      quantity: qty * (lg.qty || 1),
      limitPrice: lg.mid,
      instrumentType: "OPTION",
      orderType: "LIMIT",
      openClose: "OPEN",
      // Real contract details so a zbucks fill stores a readable position
      // (same fix as the single-leg ExecuteStep) instead of a bare OSI.
      ticker, strike: lg.strike, optionType: lg.optionType, expiry: build.expiry,
    }));
    setConfirmLegs(legs);
  };

  // Called after the multi-leg modal finishes placing every leg (success/fail).
  const onConfirmedLegs = (results) => {
    const byIndex = results || [];
    setProgress(byIndex.map(r => ({ leg: r.leg, ok: r.ok, error: r.error })));
    const allOk = byIndex.length === build.legs.length && byIndex.every(r => r.ok);
    if (allOk) hapticSuccess(); else hapticError();
    setPlacing(false);
    onResult({
      success: allOk,
      combo: true,
      strategy,
      orderId: byIndex.map(r => r.order?.orderId).filter(Boolean).join(", "),
      legs: byIndex,
      error: allOk ? null : byIndex.find(r => !r.ok)?.error,
      quantity: qty,
      netCost: build.netCost * qty,
      isCredit,
    });
  };

  const sideColor = (s) => s === "SELL" ? "#f87171" : "#4ade80";

  return (
    <div style={{ padding: "0 0 200px" }} data-testid="multi-execute-step">
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Multi-Leg Combo</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>{strategy.name}</div>
        <div style={{ fontSize: 12, color: "#636e7b", marginTop: 4 }}>{ticker} · Exp {build.expiry} · {build.legs.length} legs</div>
      </div>

      {/* Net cost */}
      <div style={{ textAlign: "center", padding: "18px 0", marginBottom: 16, borderRadius: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontSize: 11, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Net {isCredit ? "Credit" : "Debit"} · {qty} combo{qty > 1 ? "s" : ""}</div>
        <div style={{ fontSize: 36, fontWeight: 800, color: isCredit ? "#4ade80" : "#e6edf3", lineHeight: 1.1, marginTop: 4 }}>{isCredit ? "+" : ""}{fundingSource === "zbucks" ? "Ⓩ" : "$"}{fN(totalCost, 2)}</div>
        <div style={{ fontSize: 11, color: "#636e7b", marginTop: 2 }}>{fundingSource === "zbucks" ? "Ⓩ" : "$"}{fN(Math.abs(build.netCost), 2)} per combo</div>
      </div>

      {/* Legs */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 0.5, marginBottom: 8 }}>LEG BREAKDOWN</div>
        {build.legs.map((lg, i) => {
          const p = progress[i];
          return (
            <div key={i} data-testid={`combo-leg-${i}`} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", marginBottom: 6, borderRadius: 12,
              background: "rgba(255,255,255,0.02)", border: `1px solid ${p?.ok ? "rgba(74,222,128,0.24)" : p?.ok === false ? "rgba(239,68,68,0.22)" : "rgba(255,255,255,0.05)"}`,
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: sideColor(lg.side), width: 42, textAlign: "center", padding: "4px 0", borderRadius: 6, background: `${sideColor(lg.side)}15` }}>{lg.side}</span>
              <span style={{ fontSize: 11, color: "#636e7b", width: 30 }}>{(lg.qty || 1) * qty}x</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>${lg.strike} {lg.optionType === "put" ? "Put" : "Call"}</div>
                <div style={{ fontSize: 9, color: "#636e7b", fontFamily: "monospace", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{lg.osi}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, color: "#c9d1d9", fontWeight: 700 }}>${fN(lg.mid, 2)}</div>
                {p && <div style={{ fontSize: 9, color: p.ok ? "#4ade80" : "#f87171", marginTop: 2 }}>{p.ok ? "✓ Filled" : "× Failed"}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Qty stepper */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28, marginBottom: 16 }}>
        <button onClick={() => { hapticTap(); setQty(q => Math.max(1, q - 1)); }} data-testid="combo-qty-minus" disabled={placing} style={{ width: 44, height: 44, borderRadius: 22, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: placing ? "default" : "pointer", fontWeight: 600, opacity: placing ? 0.4 : 1 }}>−</button>
        <div style={{ textAlign: "center" }}><div style={{ fontSize: 11, color: "#636e7b" }}>Combos</div><div style={{ fontSize: 30, fontWeight: 800, color: "#e6edf3" }}>{qty}</div></div>
        <button onClick={() => { hapticTap(); setQty(q => q + 1); }} data-testid="combo-qty-plus" disabled={placing} style={{ width: 44, height: 44, borderRadius: 22, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: placing ? "default" : "pointer", fontWeight: 600, opacity: placing ? 0.4 : 1 }}>+</button>
      </div>

      {/* Warning about sequential legs */}
      <div style={{ padding: 12, borderRadius: 10, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.18)", marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: "#fbbf24", fontWeight: 700, marginBottom: 4 }}>LEGGING IN · Sequential execution</div>
        <div style={{ fontSize: 11, color: "#c9d1d9", lineHeight: 1.5 }}>Each leg is placed one at a time. If any leg fails, remaining legs are cancelled. Market conditions may cause slippage.</div>
      </div>

      {/* Sticky buy */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px calc(24px + env(safe-area-inset-bottom))", background: "linear-gradient(transparent, #0d1117 8%)", zIndex: 10 }}>
        {/* Funding source — real USD buying power vs real zBucks balance */}
        <div data-testid="otf-combo-funding-toggle" style={{ display: "flex", gap: 6, padding: 4, borderRadius: 14, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", marginBottom: 10 }}>
          {[
            { key: "usd", label: "USD", sub: usdBuyingPower != null ? `$${fN(usdBuyingPower, 0)} BP` : "—", color: "#4ade80" },
            { key: "zbucks", label: "zBucks", sub: zbucksBalance != null ? `Ⓩ${fN(zbucksBalance, 0)}` : "—", color: "#fbbf24" },
          ].map(opt => (
            <button key={opt.key} disabled={placing} onClick={() => { hapticTap(); setFundingSource(opt.key); }}
              data-testid={`otf-combo-fund-${opt.key}`}
              style={{
                flex: 1, padding: "9px 10px", borderRadius: 10, border: "none", cursor: placing ? "default" : "pointer",
                background: fundingSource === opt.key ? `${opt.color}22` : "transparent",
                color: fundingSource === opt.key ? opt.color : "#8b949e", opacity: placing ? 0.6 : 1,
              }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{opt.label}</div>
              <div style={{ fontSize: 9.5, marginTop: 1, opacity: 0.85 }}>{opt.sub}</div>
            </button>
          ))}
        </div>
        <button onClick={placeCombo} disabled={placing} data-testid="place-combo-btn" style={{
          width: "100%", padding: "16px", borderRadius: 30,
          background: placing ? "#636e7b" : fundingSource === "zbucks" ? "#f59e0b" : "#16a34a",
          color: "#fff", fontSize: 16, fontWeight: 700, border: "none",
          cursor: placing ? "default" : "pointer",
          boxShadow: placing ? "none" : fundingSource === "zbucks" ? "0 6px 20px rgba(245,158,11,0.35)" : "0 6px 20px rgba(22,163,74,0.35)",
        }}>{placing ? `Placing leg ${progress.length + 1} of ${build.legs.length}…` : `Place ${qty} ${strategy.name} Combo${qty > 1 ? "s" : ""}`}</button>
      </div>

      {confirmLegs && (
        <MultiLegExecutionConfirmModal
          legs={confirmLegs}
          strategyLabel={`${ticker} · ${strategy.name}`}
          ticker={ticker}
          netCost={build.netCost * qty}
          fundingSource={fundingSource}
          onClose={() => { setConfirmLegs(null); setPlacing(false); }}
          onConfirmed={(r) => { setConfirmLegs(null); onConfirmedLegs(r); }}
        />
      )}
    </div>
  );
}

// ═══ MAIN: OPTIONS TRADING FLOW ═════════════════════════════════════════════
export default function OptionsTradingFlow({ onClose, initialTicker = "" }) {
  const [step, setStep] = useState(initialTicker ? "symbol" : "search");
  const [ticker, setTicker] = useState(initialTicker);
  const [contract, setContract] = useState(null);
  const [result, setResult] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [symbolScan, setSymbolScan] = useState(null);
  const [multiBuild, setMultiBuild] = useState(null);

  const handleSelectTicker = (t) => { setTicker(t); setStep("symbol"); };
  const handleTradeOptions = (scan) => { setSymbolScan(scan || null); setStep("strategy"); };
  const handleSelectStrategy = (s) => { setStrategy(s); setStep("strategy-summary"); };
  const handleContinueFromSummary = () => setStep("chain");
  const handlePlaceMulti = (build) => { setMultiBuild(build); setStep("execute-multi"); };
  const handleSelectContract = (c) => { setContract(c); setStep("execute"); };
  const handleResult = (r) => { setResult(r); setStep("result"); };
  const handleBackToSymbol = () => { setStep("symbol"); setContract(null); setResult(null); setStrategy(null); setMultiBuild(null); };

  const headerBack = () => {
    if (step === "result") handleBackToSymbol();
    else if (step === "execute") setStep("chain");
    else if (step === "execute-multi") setStep("strategy-summary");
    else if (step === "chain") setStep(strategy ? "strategy-summary" : "strategy");
    else if (step === "strategy-summary") setStep("strategy");
    else if (step === "strategy") setStep("symbol");
    else if (step === "symbol") { setStep("search"); setTicker(""); setSymbolScan(null); }
    else onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10001, background: "#0d1117", overflowY: "auto", WebkitOverflowScrolling: "touch" }} data-testid="options-trading-flow">
      {/* STICKY header — back button always reachable no matter how far user scrolls.
          Top padding includes the safe-area inset so the close/back buttons
          clear the notch/Dynamic Island instead of sitting under it, where
          they're both visually clipped and hard to tap without triggering
          iOS's own top-edge Control Center/Notification Center gesture. */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "#0d1117", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "calc(14px + env(safe-area-inset-top)) 16px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
        <button onClick={() => { hapticTap(); headerBack(); }} data-testid="flow-back" aria-label="Back" style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 44, height: 44, borderRadius: 22,
          background: "rgba(96,165,250,0.14)", border: "1px solid rgba(96,165,250,0.28)",
          color: "#60a5fa", cursor: "pointer", padding: 0,
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#e6edf3", textAlign: "center" }}>
          {step !== "search" ? (
            <><div>${ticker}</div><div style={{ fontSize: 10, color: "#636e7b", fontWeight: 500 }}>{{ symbol: "Quote", strategy: "Strategy", "strategy-summary": strategy?.name || "Strategy", chain: "Options", execute: "Review", "execute-multi": "Combo", result: result?.success ? "Submitted" : "Failed" }[step]}</div></>
          ) : ""}
        </div>
        <button onClick={() => { hapticTap(); onClose(); }} data-testid="flow-close" aria-label="Close" style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 20,
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
          color: "#e6edf3", fontSize: 20, cursor: "pointer", padding: 0,
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
        }}>&times;</button>
      </div>

      <div style={{ padding: "0 16px" }}>
        {step === "search" && <SearchStep onSelect={handleSelectTicker} />}
        {step === "symbol" && <SymbolStep ticker={ticker} onBack={() => { setStep("search"); setTicker(""); }} onTradeOptions={handleTradeOptions} />}
        {step === "strategy" && <StrategyPickerStep ticker={ticker} scan={symbolScan} onSelectStrategy={handleSelectStrategy} onBack={() => setStep("symbol")} />}
        {step === "strategy-summary" && strategy && <StrategySummary strategy={strategy} ticker={ticker} onContinue={handleContinueFromSummary} onBack={() => setStep("strategy")} onPlaceMulti={handlePlaceMulti} />}
        {step === "chain" && <ChainStep ticker={ticker} onBack={() => setStep(strategy ? "strategy-summary" : "strategy")} onSelectContract={handleSelectContract} />}
        {step === "execute" && <ExecuteStep ticker={ticker} contract={contract} onBack={() => setStep("chain")} onResult={handleResult} />}
        {step === "execute-multi" && multiBuild && strategy && <MultiLegExecuteStep build={multiBuild} ticker={ticker} strategy={strategy} onBack={() => setStep("strategy-summary")} onResult={handleResult} />}
        {step === "result" && <ResultStep result={result} ticker={ticker} onBackToSymbol={handleBackToSymbol} />}
      </div>
    </div>
  );
}
